import { Mail, Phone } from 'lucide-react'
import { CanonicalLoad } from '@numeo/types-core'
import { ManualChatIcon } from 'components/icons/DispatchIcons'
import { BoxTruckEmailThread } from '../hooks/useBoxTruckOverlays'
import { brokerEmail, brokerPhone } from '../utils/loadFields'

interface BoxTruckContactCellProps {
    load: CanonicalLoad
    /** The load's bid-email thread, when one exists — flips the compose icon to the green thread icon. */
    thread?: BoxTruckEmailThread
    /** Opens the load drawer (compose before a send, Email Chat after). */
    onOpen: (load: CanonicalLoad) => void
}

/**
 * The Contact cell's content — the Load Hub contact-column logic, with the email/phone strings shown inline
 * (not just tooltips): compose (mail) icon + address until a bid email is sent, then the green thread icon
 * (+ red unread-reply badge) + address — either opens the load drawer. Phone is a direct tel: link with the
 * number. Everything stops propagation so a click never doubles as a row click. Extracted from the table row
 * so the loadboard and Bidding tables share ONE implementation of this non-trivial cell.
 */
export const BoxTruckContactCell = ({ load, thread, onOpen }: BoxTruckContactCellProps) => {
    const email = brokerEmail(load)
    const phone = brokerPhone(load)
    if (!email && !phone && !thread) return <span className="text-foreground-muted">—</span>
    return (
        <div className="flex flex-col gap-0.5 text-xs">
            {(thread || email) && (
                <button
                    type="button"
                    title={thread ? `Open email thread${email ? ` — ${email}` : ''}` : `Compose email to ${email}`}
                    aria-label={thread ? 'Open email thread' : `Compose email to ${email}`}
                    onClick={(e) => {
                        e.stopPropagation()
                        onOpen(load)
                    }}
                    className="inline-flex cursor-pointer items-center gap-1.5 rounded border-none bg-transparent p-0 text-left transition-colors hover:underline"
                >
                    {thread ? (
                        <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center text-success">
                            <ManualChatIcon color="green" className="h-3.5 w-3.5" />
                            {thread.unreadCount > 0 && (
                                <span className="absolute -right-1.5 -top-1.5 inline-flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-danger px-0.5 text-[9px] font-bold leading-none text-background">
                                    {thread.unreadCount}
                                </span>
                            )}
                        </span>
                    ) : (
                        <Mail size={13} className="shrink-0 text-accent" />
                    )}
                    <span className={`max-w-[170px] truncate ${thread ? 'text-success' : 'text-accent'}`}>{email || 'Email thread'}</span>
                </button>
            )}
            {phone && (
                <a
                    href={`tel:${phone}`}
                    title={`Call ${phone}`}
                    aria-label={`Call ${phone}`}
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1.5 text-accent no-underline transition-colors hover:underline"
                >
                    <Phone size={13} className="shrink-0" />
                    <span className="max-w-[170px] truncate">{phone}</span>
                </a>
            )}
        </div>
    )
}
